/**
 * Landing screen. This is where content writers start: they need a schema
 * (uploaded or picked from a saved project) before the editor makes sense.
 *
 * The schema + content separation matches the developer/writer roles:
 *  - Developer authors the schema out-of-band and hands it to the writer.
 *  - Writer lands here, uploads the schema (maybe once, then reopens), and
 *    either starts with empty content or uploads an initial document.
 *
 * Uploads are validated as JSON. We do NOT deep-validate the schema against
 * a meta-schema here — that's the engine's job on first render, where
 * structured errors give the writer a useful message.
 *
 * Mode-aware: pulls projects through `useProjectStore()`, so the same
 * component lists localStorage projects in free mode and cloud projects
 * in paid mode. Demo handling is free-only (a built-in fixture, not a
 * cloud-stored project).
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useProjectStore } from '../data/StoreContext.js';
import { demoSchema } from '../demo-fixture.js';
import { downloadJson } from '../editor-actions.js';
import { createProjectId, dismissDemo, isDemoDismissed, type StoredProject } from '../projects.js';
import type { Project, ProjectSummary } from '../stores/types.js';

import { BliksysMark } from './BliksysMark.js';
import { BringYourProjects } from './BringYourProjects.js';
import { labelDeployKind } from './DeployTargetField.js';
import { Icon } from './Icon.js';
import './Landing.css';
import { NewProjectDialog, type NewProjectConfig } from './NewProjectDialog.js';
import { ProjectSettings } from './ProjectSettings.js';
import { UserMenu } from './UserMenu.js';

interface Props {
  onOpen: (project: Project) => void;
  /** Open an existing project directly in the schema editor. */
  onOpenSchema: (project: Project) => void;
  onOpenDemo: () => void;
  /** Create a blank project and open it directly in schema-edit mode. */
  onOpenBlank: (project: Project) => void;
  /** Navigate to the privacy notice. */
  onOpenPrivacy: () => void;
  /** Navigate to the FAQ page. */
  onOpenFaq: () => void;
}

/**
 * Project-creation state machine. Both creation paths funnel through
 * the NewProjectDialog so the user names the project + picks deploy
 * target before the editor opens. Cancel returns to idle.
 */
type CreationState =
  | { kind: 'idle' }
  | { kind: 'error'; message: string }
  /** Schema parsed from import; waiting for user to confirm name + deploy. */
  | { kind: 'pending-import'; schema: JsonSchema; filename: string }
  /** "Create with editor" path; blank schema, dialog open. */
  | { kind: 'pending-blank' };

export function Landing({
  onOpen,
  onOpenSchema,
  onOpenDemo,
  onOpenBlank,
  onOpenPrivacy,
  onOpenFaq,
}: Props): JSX.Element {
  const store = useProjectStore();
  const [state, setState] = useState<CreationState>({ kind: 'idle' });
  const schemaInputRef = useRef<HTMLInputElement>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ProjectSummary | null>(null);
  // Demo only exists in free mode; the cloud has no built-in demo.
  const [demoDismissed, setDemoDismissed] = useState(() =>
    store.mode === 'free' ? isDemoDismissed() : true,
  );
  const showDemo = store.mode === 'free' && !demoDismissed;

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const list = await store.list();
      setProjects(list);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load projects.');
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSchemaFile = async (file: File): Promise<void> => {
    try {
      const text = await file.text();
      const parsed: unknown = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setState({ kind: 'error', message: 'Schema must be a JSON object at its root.' });
        return;
      }
      setState({ kind: 'pending-import', schema: parsed, filename: file.name });
    } catch (err) {
      setState({
        kind: 'error',
        message: `Could not parse ${file.name}: ${err instanceof Error ? err.message : 'unknown error'}`,
      });
    }
  };

  const handleStartBlankClick = (): void => {
    setState({ kind: 'pending-blank' });
  };

  /**
   * Common confirmation tail for both pending-import and pending-blank.
   * Builds the project, persists it, and routes the caller to the right
   * editor mode (content for imports — they have a real schema and might
   * have content; schema-edit for blanks — there's nothing to edit yet).
   */
  const handleDialogConfirm = async (config: NewProjectConfig): Promise<void> => {
    if (state.kind !== 'pending-import' && state.kind !== 'pending-blank') return;
    const now = new Date().toISOString();
    const isImport = state.kind === 'pending-import';
    const schema: JsonSchema = isImport ? state.schema : { type: 'object', properties: {} };
    const data: JsonValue = isImport ? null : {};
    // Cloud mode silently ignores deployTarget/assetOrigin in the new-project
    // dialog (the form doesn't expose them in cloud mode anyway); the org's
    // pre-saved targets are linked from project settings.
    const project: Project = {
      id: createProjectId(),
      name: config.name,
      schema,
      data,
      ...(store.mode === 'free' &&
        config.deployTarget !== undefined && {
          deployTarget: config.deployTarget,
        }),
      ...(store.mode === 'free' &&
        config.assetOrigin !== undefined && {
          assetOrigin: config.assetOrigin,
        }),
      createdAt: now,
      updatedAt: now,
    };
    try {
      const saved = await store.save(project);
      setState({ kind: 'idle' });
      if (isImport) onOpen(saved);
      else onOpenBlank(saved);
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not create project.',
      });
    }
  };

  const handleDialogCancel = (): void => {
    setState({ kind: 'idle' });
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await store.delete(id);
      await refresh();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to delete project.');
    }
  };

  const handleOpenSummary = async (
    summary: ProjectSummary,
    mode: 'content' | 'schema',
  ): Promise<void> => {
    try {
      const project = await store.get(summary.id);
      if (!project) {
        setLoadError('Project no longer exists.');
        await refresh();
        return;
      }
      if (mode === 'content') onOpen(project);
      else onOpenSchema(project);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load project.');
    }
  };

  return (
    <div className="landing">
      <header className="landing-header">
        <div className="landing-logo" aria-hidden="true">
          <BliksysMark size={32} />
        </div>
        <h1 className="landing-title">Bliksys</h1>
        <p className="landing-subtitle">
          Content editing driven by a JSON Schema. Bring a schema, bring your content, edit with
          confidence.
        </p>
        {store.mode === 'cloud' && (
          <div className="landing-user-menu">
            <UserMenu />
          </div>
        )}
      </header>

      {store.mode === 'cloud' && store.orgSlug && (
        <BringYourProjects orgSlug={store.orgSlug} onImported={() => void refresh()} />
      )}

      <section className="landing-section" aria-labelledby="new-project-heading">
        <h2 id="new-project-heading" className="landing-section-title">
          Start a new project
        </h2>
        <div className="landing-start-grid">
          <button
            type="button"
            className="landing-start-card"
            onClick={() => schemaInputRef.current?.click()}
          >
            <span className="landing-start-card-icon" aria-hidden="true">
              <Icon name="import" size={20} />
            </span>
            <span className="landing-start-card-title">Import a schema file</span>
            <span className="landing-start-card-hint">
              Open an existing JSON Schema you already have on disk.
            </span>
            <input
              ref={schemaInputRef}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleSchemaFile(f);
              }}
            />
          </button>

          <button
            type="button"
            className="landing-start-card landing-start-card--green"
            onClick={handleStartBlankClick}
          >
            <span
              className="landing-start-card-icon landing-start-card-icon--green"
              aria-hidden="true"
            >
              <Icon name="pencil-square" size={20} />
            </span>
            <span className="landing-start-card-title">Create with the editor</span>
            <span className="landing-start-card-hint">
              Start blank and author your schema field by field. No JSON required.
            </span>
          </button>
        </div>

        {state.kind === 'error' && (
          <p className="landing-error" role="alert">
            {state.message}
          </p>
        )}
        {loadError && (
          <p className="landing-error" role="alert">
            {loadError}
          </p>
        )}
      </section>

      {(projects.length > 0 || showDemo) && (
        <section className="landing-section" aria-labelledby="recent-heading">
          <h2 id="recent-heading" className="landing-section-title">
            {store.mode === 'cloud' ? 'Projects' : 'Recent projects'}
          </h2>
          <ul className="landing-projects">
            {projects.map((p) => (
              <li key={p.id} className="landing-project-item">
                <button
                  type="button"
                  className="landing-project-open"
                  onClick={() => void handleOpenSummary(p, 'content')}
                >
                  <div className="landing-project-name">{p.name}</div>
                  <div className="landing-project-meta">
                    Updated {new Date(p.updatedAt).toLocaleString()}
                    {p.deployTargetKind && <> · deploys to {labelDeployKind(p.deployTargetKind)}</>}
                  </div>
                </button>
                <ProjectRowMenu
                  project={p}
                  mode={store.mode}
                  onSettings={() => setEditing(p)}
                  onOpenSchema={() => void handleOpenSummary(p, 'schema')}
                  onDownloadSchema={() => void downloadProjectFile(store, p, 'schema')}
                  onDownloadContent={() => void downloadProjectFile(store, p, 'content')}
                  onDelete={() => void handleDelete(p.id)}
                />
              </li>
            ))}
            {showDemo && (
              <li className="landing-project-item landing-project-item-demo">
                <button type="button" className="landing-project-open" onClick={onOpenDemo}>
                  <div className="landing-project-name">
                    ACME Corp. demo
                    <span className="landing-project-tag" aria-label="Read-only demo project">
                      Demo
                    </span>
                  </div>
                  <div className="landing-project-meta">
                    A small site schema with pages and blocks. Read-only.
                  </div>
                </button>
                <DemoRowMenu
                  onDownloadSchema={() => downloadDemoSchema()}
                  onDismiss={() => {
                    dismissDemo();
                    setDemoDismissed(true);
                  }}
                />
              </li>
            )}
          </ul>
        </section>
      )}

      {editing && (
        <ProjectSettingsLoader
          summary={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void refresh();
            setEditing(null);
          }}
        />
      )}

      {state.kind === 'pending-import' && (
        <NewProjectDialog
          title="Create project"
          description={`Imported ${state.filename}. Name your project and choose a deploy target.`}
          initialName={extractDefaultName(state.schema, state.filename)}
          confirmLabel="Open editor →"
          onConfirm={(c) => void handleDialogConfirm(c)}
          onCancel={handleDialogCancel}
          /* Inline deploy / asset configuration is only meaningful in free mode. */
          showDeployTarget={store.mode === 'free'}
          showAssetOrigin={store.mode === 'free'}
        />
      )}
      {state.kind === 'pending-blank' && (
        <NewProjectDialog
          title="Create project"
          description="Author your schema in the editor — no JSON required."
          initialName=""
          confirmLabel="Open editor →"
          onConfirm={(c) => void handleDialogConfirm(c)}
          onCancel={handleDialogCancel}
          showDeployTarget={store.mode === 'free'}
          showAssetOrigin={store.mode === 'free'}
        />
      )}

      <footer className="landing-footer">
        <button type="button" className="landing-footer-link" onClick={onOpenFaq}>
          FAQ
        </button>
        <span aria-hidden="true">·</span>
        <button type="button" className="landing-footer-link" onClick={onOpenPrivacy}>
          Privacy
        </button>
      </footer>
    </div>
  );
}

/**
 * Settings dialog needs the full project (with secrets, schema, content)
 * but the recent-list only has summaries. Wrapper fetches lazily when
 * the user clicks the menu's Settings item — no point fetching all
 * projects' full bodies up front.
 */
function ProjectSettingsLoader({
  summary,
  onClose,
  onSaved,
}: {
  summary: ProjectSummary;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element | null {
  const store = useProjectStore();
  const [project, setProject] = useState<Project | null>(null);
  useEffect(() => {
    let cancelled = false;
    void store.get(summary.id).then((p) => {
      if (!cancelled) setProject(p);
    });
    return () => {
      cancelled = true;
    };
  }, [store, summary.id]);
  if (!project) return null;
  return (
    <ProjectSettings project={project} open={true} onClose={onClose} onSaved={() => onSaved()} />
  );
}

/**
 * Pull a sensible default project name out of an imported schema.
 * Falls back to the filename without `.json` if the schema doesn't
 * declare a top-level title.
 */
function extractDefaultName(schema: JsonSchema, filename: string): string {
  if (typeof schema.title === 'string' && schema.title.length > 0) return schema.title;
  return filename.replace(/\.json$/i, '');
}

/**
 * Download a project's schema or content as a JSON file. Used by the
 * recent-projects row actions so the user can grab the artifacts without
 * opening the project first.
 */
async function downloadProjectFile(
  store: ReturnType<typeof useProjectStore>,
  summary: ProjectSummary,
  kind: 'schema' | 'content',
): Promise<void> {
  const project = await store.get(summary.id);
  if (!project) return;
  const value = kind === 'schema' ? project.schema : project.data;
  const filename = kind === 'schema' ? `${project.name}.schema.json` : `${project.name}.json`;
  downloadJson(value, filename);
}

/**
 * Download the demo's schema as a starting point for a real project.
 * The demo data isn't downloadable — it's just illustrative content the
 * author would replace anyway — so this only handles the schema.
 */
function downloadDemoSchema(): void {
  downloadJson(demoSchema, 'acme-demo.schema.json');
}

/**
 * Per-row "more actions" menu. A vertical-ellipsis button toggles a
 * popover with secondary actions (Settings, Open in schema editor,
 * Download schema, Download content, Delete). Keeps the row compact and
 * visually attached — no detached action cluster running off the right
 * edge.
 *
 * Rendered as a <details> element so we get keyboard + click-outside
 * dismissal for free without wiring up listeners. The dropdown closes on
 * any item click via a useEffect-free handler that toggles the open
 * state through the underlying DOM.
 */
function ProjectRowMenu({
  project,
  mode,
  onSettings,
  onOpenSchema,
  onDownloadSchema,
  onDownloadContent,
  onDelete,
}: {
  project: ProjectSummary;
  mode: 'free' | 'cloud';
  onSettings: () => void;
  onOpenSchema: () => void;
  onDownloadSchema: () => void;
  onDownloadContent: () => void;
  onDelete: () => void;
}): JSX.Element {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  // Close the menu after picking an item. We handle this on the menu
  // root rather than per-button so any future items inherit it.
  const close = (): void => {
    if (detailsRef.current) detailsRef.current.open = false;
  };

  const wrap = (handler: () => void) => (): void => {
    handler();
    close();
  };

  // Close when the user clicks anywhere outside the open menu, or presses Escape.
  useEffect(() => {
    const onDocClick = (e: MouseEvent): void => {
      if (!detailsRef.current) return;
      if (!detailsRef.current.open) return;
      if (e.target instanceof Node && !detailsRef.current.contains(e.target)) {
        detailsRef.current.open = false;
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <details ref={detailsRef} className="landing-project-menu">
      <summary
        className="landing-project-menu-toggle"
        aria-label={`More actions for ${project.name}`}
        aria-haspopup="menu"
        title="More actions"
      >
        <Icon name="more-vertical" size={16} />
      </summary>
      <div className="landing-project-menu-popover" role="menu">
        <button
          type="button"
          className="landing-project-menu-item"
          role="menuitem"
          onClick={wrap(onSettings)}
        >
          Settings
        </button>
        <button
          type="button"
          className="landing-project-menu-item"
          role="menuitem"
          onClick={wrap(onOpenSchema)}
        >
          Open in schema editor
        </button>
        {mode === 'free' && (
          <>
            <button
              type="button"
              className="landing-project-menu-item"
              role="menuitem"
              onClick={wrap(onDownloadSchema)}
            >
              Download schema
            </button>
            <button
              type="button"
              className="landing-project-menu-item"
              role="menuitem"
              onClick={wrap(onDownloadContent)}
            >
              Download content
            </button>
          </>
        )}
        <button
          type="button"
          className="landing-project-menu-item landing-project-menu-item-danger"
          role="menuitem"
          onClick={wrap(onDelete)}
        >
          Delete
        </button>
      </div>
    </details>
  );
}

/**
 * Streamlined menu for the demo row. The demo is read-only and never
 * persisted, so most of the project menu's actions don't apply:
 *  - No Settings (the demo doesn't have configurable settings).
 *  - No "Open in schema editor" (schema editing of a non-persisted
 *    sample would surprise users — the demo's purpose is to show
 *    the content editor).
 *  - No download of content (the demo data IS the schema's example,
 *    so download would just spit back boilerplate).
 * What's left: download the demo schema (handy as a starting point
 * for a real project), and dismiss it from the list.
 */
function DemoRowMenu({
  onDownloadSchema,
  onDismiss,
}: {
  onDownloadSchema?: () => void;
  onDismiss: () => void;
}): JSX.Element {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const close = (): void => {
    if (detailsRef.current) detailsRef.current.open = false;
  };
  const wrap = (handler: () => void) => (): void => {
    handler();
    close();
  };
  useEffect(() => {
    const onDocClick = (e: MouseEvent): void => {
      if (!detailsRef.current) return;
      if (!detailsRef.current.open) return;
      if (e.target instanceof Node && !detailsRef.current.contains(e.target)) {
        detailsRef.current.open = false;
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
  return (
    <details ref={detailsRef} className="landing-project-menu">
      <summary
        className="landing-project-menu-toggle"
        aria-label="More actions for the demo"
        aria-haspopup="menu"
        title="More actions"
      >
        <Icon name="more-vertical" size={16} />
      </summary>
      <div className="landing-project-menu-popover" role="menu">
        {onDownloadSchema && (
          <button
            type="button"
            className="landing-project-menu-item"
            role="menuitem"
            onClick={wrap(onDownloadSchema)}
          >
            Download schema
          </button>
        )}
        <button
          type="button"
          className="landing-project-menu-item landing-project-menu-item-danger"
          role="menuitem"
          onClick={wrap(onDismiss)}
        >
          Dismiss
        </button>
      </div>
    </details>
  );
}

// Backward-compat: the old default export passed `StoredProject` types
// to its callbacks. New code passes through `Project`. Re-export the
// alias so a transitional period of mixed callers still compiles.
export type { StoredProject };
